import React, { useState, useEffect, useRef } from 'react';
import {
  collection, onSnapshot, doc, setDoc, getDoc, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import ConfirmDialog from '../UI/ConfirmDialog';

const fmt = (ts) => {
  if (!ts?.toDate) return '—';
  const d = ts.toDate();
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60)         return 'Just now';
  if (diff < 3600)       return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)      return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7)  return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl shadow-sm p-5">
    <div className="text-2xl font-bold text-slate-800">{value}</div>
    <div className="text-sm font-medium text-slate-600 mt-0.5">{label}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const useOrgUsers = (orgId, enabled) => {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    if (!enabled || !orgId) return;
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'users'), (snap) => {
      setUsers(
        snap.docs
          .map(d => ({ email: d.id, ...d.data() }))
          .sort((a, b) => (b.lastLoginAt?.seconds || 0) - (a.lastLoginAt?.seconds || 0))
      );
    });
    return unsub;
  }, [orgId, enabled]);
  return users;
};

const TenantRow = ({ org, onToggle, onOpen }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(org.name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef(null);
  const users = useOrgUsers(org.id, expanded);
  const isActive = org.active !== false;

  useEffect(() => { setNameVal(org.name || ''); }, [org.name]);
  useEffect(() => { if (editingName) nameInputRef.current?.focus(); }, [editingName]);

  const saveName = async () => {
    const trimmed = nameVal.trim();
    if (!trimmed || trimmed === org.name) { setEditingName(false); return; }
    setNameSaving(true);
    await Promise.all([
      setDoc(doc(db, 'orgs', org.id), { name: trimmed }, { merge: true }),
      setDoc(doc(db, 'orgs', org.id, '_config', 'settings'), { orgName: trimmed }, { merge: true }),
    ]);
    setNameSaving(false);
    setEditingName(false);
  };

  return (
    <>
      <tr
        className={`hover:bg-slate-50 cursor-pointer ${!isActive ? 'opacity-60' : ''}`}
        onClick={() => setExpanded(e => !e)}
      >
        <td className="px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-xs w-3">{expanded ? '▾' : '▸'}</span>
            <div>
              {editingName ? (
                <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                  <input
                    ref={nameInputRef}
                    value={nameVal}
                    onChange={e => setNameVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                    className="border border-blue-300 rounded px-2 py-0.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-300 w-48"
                    disabled={nameSaving}
                  />
                  <button onClick={saveName} disabled={nameSaving} className="text-xs text-blue-600 font-medium hover:underline disabled:opacity-50">
                    {nameSaving ? '…' : 'Save'}
                  </button>
                  <button onClick={() => setEditingName(false)} className="text-xs text-slate-500 hover:text-slate-600">Cancel</button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 group/name">
                  <div className="font-medium text-slate-800">{org.name || <span className="text-slate-500 italic">{org.id}</span>}</div>
                  <button
                    onClick={e => { e.stopPropagation(); setEditingName(true); }}
                    className="opacity-0 group-hover/name:opacity-100 text-slate-500 hover:text-slate-600 transition-opacity"
                    aria-label="Edit name"
                    title="Edit name"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2a2 2 0 01.586-1.414z" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="text-xs text-slate-500 font-mono">{org.id}</div>
            </div>
          </div>
        </td>
        <td className="px-5 py-3 text-slate-600 text-sm text-center">{org.userCount ?? '—'}</td>
        <td className="px-5 py-3 text-slate-500 text-xs">{fmt(org.lastLoginAt)}</td>
        <td className="px-5 py-3 text-slate-500 text-xs">
          {org.createdAt?.toDate ? org.createdAt.toDate().toLocaleDateString() : '—'}
        </td>
        <td className="px-5 py-3">
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td className="px-5 py-3 text-right" onClick={e => e.stopPropagation()}>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => onOpen(org)}
              className="border border-blue-300 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-blue-50 transition-colors"
            >
              Open
            </button>
            <button
              onClick={() => onToggle(org)}
              className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50 transition-colors"
            >
              {isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} className="px-0 py-0 bg-slate-50 border-b border-gray-200">
            <div className="px-14 py-3">
              {users === null ? (
                <p className="text-xs text-slate-500 py-2">Loading users…</p>
              ) : users.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">No users found.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-slate-500 border-b border-gray-200">
                      <th className="pb-1.5 text-left font-semibold">Email</th>
                      <th className="pb-1.5 text-left font-semibold">Name</th>
                      <th className="pb-1.5 text-left font-semibold">Role</th>
                      <th className="pb-1.5 text-left font-semibold">Last Login</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {users.map(u => (
                      <tr key={u.email} className="hover:bg-white">
                        <td className="py-2 text-slate-700">{u.email}</td>
                        <td className="py-2 text-slate-500">{u.displayName || '—'}</td>
                        <td className="py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>
                            {u.role === 'admin' ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td className="py-2 text-slate-500">{fmt(u.lastLoginAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Manager = ({ onOpenOrg }) => {
  const [orgs, setOrgs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm] = useState({ orgId: '', name: '', adminEmail: '' });
  const [registerOrgId, setRegisterOrgId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmToggle, setConfirmToggle] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'orgs'), async (snap) => {
      const rows = await Promise.all(snap.docs.map(async (d) => {
        const org = { id: d.id, ...d.data() };
        try {
          const usersSnap = await getDocs(collection(db, 'orgs', d.id, 'users'));
          org.userCount = usersSnap.size;
          let latest = null;
          usersSnap.docs.forEach(u => {
            const t = u.data().lastLoginAt;
            if (t && (!latest || t.seconds > latest.seconds)) latest = t;
          });
          org.lastLoginAt = latest;
        } catch (_) { org.userCount = 0; }
        return org;
      }));
      setOrgs(rows.sort((a, b) => a.id.localeCompare(b.id)));
    });
    return unsub;
  }, []);

  const createTenant = async () => {
    const orgId = form.orgId.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    const name  = form.name.trim();
    const email = form.adminEmail.trim().toLowerCase();
    if (!orgId || !name || !email) { setError('All fields are required.'); return; }
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { setError('Enter a valid email address.'); return; }
    setSaving(true); setError('');
    try {
      const existing = await getDoc(doc(db, 'orgs', orgId));
      if (existing.exists()) { setError(`"${orgId}" already exists.`); setSaving(false); return; }
      await setDoc(doc(db, 'orgs', orgId), { name, subdomain: orgId, active: true, createdAt: serverTimestamp(), createdBy: auth.currentUser?.email || '' });
      await setDoc(doc(db, 'orgs', orgId, 'users', email), { role: 'admin' });
      setForm({ orgId: '', name: '', adminEmail: '' });
      setShowAdd(false);
      setSuccess(`"${name}" created. ${email} can now sign in.`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) { setError(err.message || 'Failed to create.'); }
    setSaving(false);
  };

  const registerExisting = async () => {
    const orgId = registerOrgId.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!orgId) { setError('Enter an org ID.'); return; }
    setSaving(true); setError('');
    try {
      // Check if there are any users in this org
      const usersSnap = await getDocs(collection(db, 'orgs', orgId, 'users'));
      if (usersSnap.empty) { setError(`No users found under org ID "${orgId}". Check the ID and try again.`); setSaving(false); return; }
      await setDoc(doc(db, 'orgs', orgId), { name: orgId, subdomain: orgId, active: true, createdAt: serverTimestamp() }, { merge: true });
      setRegisterOrgId('');
      setShowRegister(false);
      setSuccess(`"${orgId}" registered with ${usersSnap.size} existing user(s).`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) { setError(err.message || 'Failed to register.'); }
    setSaving(false);
  };

  const toggleActive = async (org) => {
    await setDoc(doc(db, 'orgs', org.id), { active: !(org.active !== false) }, { merge: true });
    setConfirmToggle(null);
  };

  const totalUsers = orgs.reduce((s, o) => s + (o.userCount || 0), 0);
  const activeOrgs = orgs.filter(o => o.active !== false).length;
  const latestLogin = orgs.reduce((best, o) => {
    const s = o.lastLoginAt?.seconds || 0;
    return s > (best?.seconds || 0) ? o.lastLoginAt : best;
  }, null);

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Tenant Manager</h2>
          <p className="text-slate-500 text-sm mt-0.5">Manage organizations and monitor activity</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowRegister(s => !s); setShowAdd(false); setError(''); }}
            className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Register Existing
          </button>
          <button
            onClick={() => { setShowAdd(s => !s); setShowRegister(false); setError(''); }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            + New Tenant
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Tenants" value={orgs.length} sub={`${activeOrgs} active`} />
        <StatCard label="Total Users" value={totalUsers} sub="across all orgs" />
        <StatCard label="Most Recent Login" value={fmt(latestLogin)} sub="any tenant" />
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 rounded-xl mb-5">{success}</div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-xl mb-5">{error}</div>
      )}

      {/* Register existing org */}
      {showRegister && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-amber-100">
          <h3 className="font-semibold text-slate-800 mb-1">Register Existing Org</h3>
          <p className="text-slate-500 text-xs mb-4">Use this to bring an existing org (with users already in Firestore) into the Manager.</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Org ID</label>
              <input
                value={registerOrgId}
                onChange={e => setRegisterOrgId(e.target.value)}
                placeholder="e.g. sidneybaptist"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <button onClick={registerExisting} disabled={saving} className="bg-amber-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50">
              {saving ? 'Registering…' : 'Register'}
            </button>
            <button onClick={() => setShowRegister(false)} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* New tenant form */}
      {showAdd && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-blue-100">
          <h3 className="font-semibold text-slate-800 mb-4">New Tenant</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Org ID *</label>
              <input
                value={form.orgId}
                onChange={e => setForm(f => ({ ...f, orgId: e.target.value }))}
                placeholder="firstbaptist"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Organization Name *</label>
              <input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="First Baptist Church"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Initial Admin Email *</label>
              <input
                type="email"
                value={form.adminEmail}
                onChange={e => setForm(f => ({ ...f, adminEmail: e.target.value }))}
                placeholder="pastor@example.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createTenant} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Creating…' : 'Create Tenant'}
            </button>
            <button onClick={() => { setShowAdd(false); setError(''); }} className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tenant table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-gray-200 text-slate-600">
              <th className="px-5 py-3 text-left font-semibold">Organization</th>
              <th className="px-5 py-3 text-center font-semibold">Users</th>
              <th className="px-5 py-3 text-left font-semibold">Last Login</th>
              <th className="px-5 py-3 text-left font-semibold">Created</th>
              <th className="px-5 py-3 text-left font-semibold">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {orgs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-slate-500">
                  No tenants found. Use <strong>Register Existing</strong> to bring in an existing org, or <strong>+ New Tenant</strong> to create one.
                </td>
              </tr>
            ) : orgs.map(org => (
              <TenantRow key={org.id} org={org} onToggle={setConfirmToggle} onOpen={onOpenOrg} />
            ))}
          </tbody>
        </table>
      </div>

      {confirmToggle && (
        <ConfirmDialog
          title={confirmToggle.active !== false ? `Deactivate "${confirmToggle.name || confirmToggle.id}"?` : `Activate "${confirmToggle.name || confirmToggle.id}"?`}
          message={confirmToggle.active !== false ? 'All users in this organization will be locked out immediately.' : 'Users in this organization will be able to sign in again.'}
          onConfirm={() => toggleActive(confirmToggle)}
          onCancel={() => setConfirmToggle(null)}
          confirmLabel={confirmToggle.active !== false ? 'Deactivate' : 'Activate'}
          danger={confirmToggle.active !== false}
        />
      )}
    </div>
  );
};

export default Manager;
